import { DEFAULT_CYCLE_LENGTH, MIN_CYCLE_LENGTH, MAX_CYCLE_LENGTH, NEW_PERIOD_GAP_DAYS } from '../constants/cycleHealth.js';
import { isActiveBleeding, isPeriodBleeding, isSpottingOnly } from '../types/healthLog.js';
import { getConfidenceTier } from './cycleConfidence.js';
const PHASE_COLORS = {
    menstrual: '#EC4899',
    follicular: '#F97316',
    ovulatory: '#22C55E',
    luteal: '#8B5CF6',
};
const PHASE_LABELS = {
    menstrual: 'Menstrual',
    follicular: 'Follicular',
    ovulatory: 'Ovulatory',
    luteal: 'Luteal',
};
export function getPhaseColor(phase) {
    return phase ? PHASE_COLORS[phase] : '#D1D5DB';
}
export function getPhaseLabel(phase) {
    return phase ? PHASE_LABELS[phase] : 'Unknown';
}
function toDateStr(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function daysBetween(a, b) {
    const da = new Date(a + 'T00:00:00');
    const db = new Date(b + 'T00:00:00');
    return Math.round((db.getTime() - da.getTime()) / (1000 * 60 * 60 * 24));
}
function addDays(dateStr, n) {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + n);
    return toDateStr(d);
}
/**
 * Detect period start dates from health log entries.
 * Single source of truth for period detection — used by cycle stats,
 * cycle prediction, and the cycle facet engine.
 *
 * Rules:
 * - Only considers period bleeding (light/medium/heavy, not spotting)
 * - Deduplicates by date (last entry per date wins)
 * - A new period start requires a gap >= NEW_PERIOD_GAP_DAYS from prior bleeding
 *   AND >= MIN_CYCLE_LENGTH from the last period start
 */
export function detectPeriodStarts(cycleEntries) {
    const dateMap = new Map();
    for (const e of cycleEntries)
        dateMap.set(e.date, e);
    const bleedingDates = [...dateMap.values()]
        .filter((e) => isPeriodBleeding(e))
        .map((e) => e.date)
        .sort();
    if (bleedingDates.length === 0)
        return [];
    const periodStartDates = [bleedingDates[0]];
    for (let i = 1; i < bleedingDates.length; i++) {
        const gap = daysBetween(bleedingDates[i - 1], bleedingDates[i]);
        if (gap >= NEW_PERIOD_GAP_DAYS) {
            const lastStart = periodStartDates[periodStartDates.length - 1];
            const gapFromLastPeriod = daysBetween(lastStart, bleedingDates[i]);
            if (gapFromLastPeriod >= MIN_CYCLE_LENGTH) {
                periodStartDates.push(bleedingDates[i]);
            }
        }
    }
    return periodStartDates;
}
/**
 * Phase day boundaries for a cycle. SINGLE SOURCE OF TRUTH for the phase model —
 * physiologically accurate: the luteal phase is ~14 days fixed at the end, the
 * follicular phase absorbs cycle-length variation (NOT a proportional scaling).
 * Everything that needs phase ranges (getPhaseForDay, the chat context's phase
 * description, etc.) derives from this so the model can't diverge.
 */
export function getPhaseBoundaries(cycleLength, periodLength) {
    const lutealStart = cycleLength - 13; // ~14 day luteal phase
    return {
        menstrualEnd: periodLength ?? 5,
        ovulatoryStart: lutealStart - 2, // ovulation ~2 days before luteal
        ovulatoryEnd: lutealStart - 1,
        lutealStart,
    };
}
/** Calendar-method ovulation day: ~14 days before the next period (floored at 10). */
export function calendarOvulationDay(cycleLength) {
    return Math.max(10, cycleLength - 14);
}
export function getPhaseForDay(cycleDay, cycleLength, periodLength) {
    const { menstrualEnd, ovulatoryStart, ovulatoryEnd } = getPhaseBoundaries(cycleLength, periodLength);
    if (cycleDay >= 1 && cycleDay <= menstrualEnd)
        return 'menstrual';
    if (cycleDay < ovulatoryStart)
        return 'follicular';
    if (cycleDay <= ovulatoryEnd)
        return 'ovulatory';
    return 'luteal';
}
/**
 * Normalize a raw "days since a cycle start" count into a cycle day in
 * [1, averageCycleLength], rolling forward through un-logged cycles when the
 * raw day exceeds the anchoring cycle's length. This app is prediction-first:
 * a logging gap means the user didn't log, NOT that the cycle stopped — so we
 * project predicted cycles forward using the average length.
 *
 * SINGLE SOURCE OF TRUTH for cycle-day rollover — both the ring prediction
 * (computeCyclePrediction) and the calendar (getCalendarDaysForMonth) call
 * this, so they can never disagree about "where am I in my cycle."
 *
 * @param rawCycleDay 1-based day count since the cycle start (date - start + 1)
 * @param cycleLength the anchoring cycle's own length (actual when known, else average)
 * @param averageCycleLength the average used to project predicted cycles forward
 * @returns the normalized cycle day, and whether it was projected (gap rollover)
 */
export function resolveCycleDay(rawCycleDay, cycleLength, averageCycleLength) {
    if (rawCycleDay <= cycleLength) {
        return { cycleDay: Math.max(1, rawCycleDay), projected: false };
    }
    const overflow = rawCycleDay - cycleLength;
    const cycleDay = ((overflow - 1) % averageCycleLength) + 1;
    return { cycleDay: Math.max(1, cycleDay), projected: true };
}
// ─── Fertile Window Detection ──────────────────────────────────────
/**
 * Detect BBT thermal shift using the cover-line method.
 * Looks for 3 consecutive temps ≥0.2°F above the previous 6 temps' highest value.
 * Returns the cycle day of the shift (ovulation ≈ shift day - 1), or null.
 */
function detectBBTShift(cycleEntries, lastPeriodStart) {
    // Get BBT entries for the current cycle, sorted by date
    const bbtEntries = cycleEntries
        .filter(e => e.bbt != null && e.date >= lastPeriodStart)
        .sort((a, b) => a.date.localeCompare(b.date));
    if (bbtEntries.length < 9)
        return null; // Need 6 baseline + 3 elevated
    const temps = bbtEntries.map(e => ({ day: daysBetween(lastPeriodStart, e.date) + 1, temp: e.bbt }));
    // Sliding window: check if temps[i], temps[i+1], temps[i+2] are all ≥0.2°F above
    // the highest of the preceding 6 temps
    for (let i = 6; i <= temps.length - 3; i++) {
        const baseline = temps.slice(Math.max(0, i - 6), i);
        const coverLine = Math.max(...baseline.map(t => t.temp));
        const threshold = coverLine + 0.2;
        if (temps[i].temp >= threshold &&
            temps[i + 1]?.temp >= threshold &&
            temps[i + 2]?.temp >= threshold) {
            return temps[i].day; // Shift day; ovulation was ~day before
        }
    }
    return null;
}
/**
 * Detect cervical mucus peak — the last day of eggwhite/watery CM
 * before a transition to dry/sticky/creamy. Returns cycle day or null.
 */
function detectCMPeak(cycleEntries, lastPeriodStart) {
    const cmEntries = cycleEntries
        .filter(e => e.cervicalMucus && e.date >= lastPeriodStart)
        .sort((a, b) => a.date.localeCompare(b.date));
    if (cmEntries.length < 3)
        return null;
    const fertileTypes = new Set(['eggwhite', 'watery']);
    let lastFertileDay = null;
    for (let i = 0; i < cmEntries.length; i++) {
        const cm = cmEntries[i].cervicalMucus;
        if (fertileTypes.has(cm)) {
            // Check if the next entry is non-fertile (transition point)
            const next = cmEntries[i + 1];
            if (next && !fertileTypes.has(next.cervicalMucus)) {
                lastFertileDay = daysBetween(lastPeriodStart, cmEntries[i].date) + 1;
            }
            else if (!next) {
                // Last entry is fertile — could be peak but no confirmation yet
                lastFertileDay = daysBetween(lastPeriodStart, cmEntries[i].date) + 1;
            }
        }
    }
    return lastFertileDay;
}
/**
 * Detect OPK peak — the first 'peak' result in the current cycle.
 * Returns cycle day or null.
 */
function detectOPKPeak(cycleEntries, lastPeriodStart) {
    const opkPeak = cycleEntries
        .filter(e => e.ovulationTest === 'peak' && e.date >= lastPeriodStart)
        .sort((a, b) => a.date.localeCompare(b.date))[0];
    if (!opkPeak)
        return null;
    return daysBetween(lastPeriodStart, opkPeak.date) + 1;
}
/**
 * Estimate fertile window and ovulation day using symptothermal method
 * (BBT shift + CM peak + OPK) with calendar method fallback.
 */
function estimateFertileWindow(cycleEntries, lastPeriodStart, averageCycleLength) {
    const bbtShiftDay = detectBBTShift(cycleEntries, lastPeriodStart);
    const cmPeakDay = detectCMPeak(cycleEntries, lastPeriodStart);
    const opkPeakDay = detectOPKPeak(cycleEntries, lastPeriodStart);
    // Collect signals: BBT shift means ovulation was ~1 day before shift,
    // CM peak ≈ ovulation day, OPK peak ≈ 1-2 days before ovulation
    const signals = [];
    if (bbtShiftDay)
        signals.push(bbtShiftDay - 1); // ovulation = day before shift
    if (cmPeakDay)
        signals.push(cmPeakDay);
    if (opkPeakDay)
        signals.push(opkPeakDay + 1); // ovulation ~1 day after peak
    // If 2+ signals agree within 2 days, use their average (symptothermal)
    if (signals.length >= 2) {
        signals.sort((a, b) => a - b);
        const spread = signals[signals.length - 1] - signals[0];
        if (spread <= 2) {
            const ovulationDay = Math.round(signals.reduce((a, b) => a + b, 0) / signals.length);
            return {
                ovulationDay,
                fertileStart: ovulationDay - 5,
                fertileEnd: ovulationDay,
                method: 'symptothermal',
            };
        }
    }
    // Single strong signal
    if (signals.length === 1) {
        const ovulationDay = signals[0];
        return {
            ovulationDay,
            fertileStart: ovulationDay - 5,
            fertileEnd: ovulationDay,
            method: 'symptothermal',
        };
    }
    // Calendar fallback: ovulation ≈ cycleLength - 14
    const ovulationDay = calendarOvulationDay(averageCycleLength);
    return {
        ovulationDay,
        fertileStart: ovulationDay - 5,
        fertileEnd: ovulationDay,
        method: 'calendar',
    };
}
/**
 * Compute stats from detected period start dates.
 * Requires at least 2 period starts (1 complete cycle).
 */
export function computeCycleStats(cycleEntries) {
    const periodStartDates = detectPeriodStarts(cycleEntries);
    if (periodStartDates.length < 2)
        return null;
    // Rebuild bleeding dates for period-length counting
    const statsMap = new Map();
    for (const e of cycleEntries)
        statsMap.set(e.date, e);
    const bleedingDates = [...statsMap.values()]
        .filter((e) => isPeriodBleeding(e))
        .map((e) => e.date)
        .sort();
    // Period length per start date: count consecutive bleeding days
    const periodLengthByStart = new Map();
    for (const startDate of periodStartDates) {
        let count = 0;
        for (const bd of bleedingDates) {
            if (bd >= startDate) {
                const gap = daysBetween(startDate, bd);
                if (gap <= 10)
                    count++;
                else
                    break;
            }
        }
        if (count > 0)
            periodLengthByStart.set(startDate, count);
    }
    // Compute cycle lengths between consecutive period starts
    const cycleLengths = [];
    for (let i = 1; i < periodStartDates.length; i++) {
        const length = daysBetween(periodStartDates[i - 1], periodStartDates[i]);
        if (length >= MIN_CYCLE_LENGTH && length <= MAX_CYCLE_LENGTH) {
            cycleLengths.push({
                startDate: periodStartDates[i - 1],
                length,
                periodLength: periodLengthByStart.get(periodStartDates[i - 1]) ?? 5,
            });
        }
    }
    if (cycleLengths.length === 0)
        return null;
    const lengths = cycleLengths.map(c => c.length);
    const avg = Math.round(lengths.reduce((a, b) => a + b, 0) / lengths.length);
    const min = Math.min(...lengths);
    const max = Math.max(...lengths);
    const variation = max - min;
    // Regularity: ≤3 day variation = regular, ≤7 = somewhat, >7 = irregular
    const regularity = variation <= 3 ? 'regular' : variation <= 7 ? 'somewhat_irregular' : 'irregular';
    const periodLengths = [...periodLengthByStart.values()];
    const avgPeriodLength = periodLengths.length > 0
        ? Math.round(periodLengths.reduce((a, b) => a + b, 0) / periodLengths.length)
        : 5;
    return {
        totalCycles: cycleLengths.length,
        averageLength: avg,
        shortestLength: min,
        longestLength: max,
        averagePeriodLength: avgPeriodLength,
        regularity,
        recentCycles: cycleLengths.slice(-6),
    };
}
export function computeCyclePrediction(cycleEntries, hasSetupData = false, todayOverride, contraception) {
    const periodStartDates = detectPeriodStarts(cycleEntries);
    if (periodStartDates.length === 0)
        return null;
    // Rebuild bleeding dates for period-length counting
    const predMap = new Map();
    for (const e of cycleEntries)
        predMap.set(e.date, e);
    const bleedingDates = [...predMap.values()]
        .filter((e) => isPeriodBleeding(e))
        .map((e) => e.date)
        .sort();
    // Average cycle length from consecutive period starts
    let averageCycleLength = DEFAULT_CYCLE_LENGTH;
    if (periodStartDates.length >= 2) {
        const gaps = [];
        for (let i = 1; i < periodStartDates.length; i++) {
            const gap = daysBetween(periodStartDates[i - 1], periodStartDates[i]);
            if (gap >= MIN_CYCLE_LENGTH && gap <= MAX_CYCLE_LENGTH) {
                gaps.push(gap);
            }
        }
        if (gaps.length > 0) {
            averageCycleLength = Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length);
        }
    }
    // Average period length: count consecutive bleeding days per period start
    const periodLengths = [];
    for (const startDate of periodStartDates) {
        let count = 0;
        for (const bd of bleedingDates) {
            if (bd >= startDate) {
                const gap = daysBetween(startDate, bd);
                if (gap <= 10)
                    count++;
                else
                    break;
            }
        }
        if (count > 0)
            periodLengths.push(count);
    }
    const averagePeriodLength = periodLengths.length > 0
        ? Math.round(periodLengths.reduce((a, b) => a + b, 0) / periodLengths.length)
        : 5;
    const lastPeriodStart = periodStartDates[periodStartDates.length - 1];
    const today = todayOverride || toDateStr(new Date());
    const rawCycleDay = daysBetween(lastPeriodStart, today) + 1;
    // For the current cycle, use actual contiguous bleeding days rather than
    // the cross-cycle average — actual data beats predictions. This ensures
    // the phase ring and current phase reflect reality if this cycle's period
    // is longer or shorter than average.
    const currentCycleBleeding = bleedingDates.filter(d => d >= lastPeriodStart);
    let actualCurrentPeriodEnd = 0;
    if (currentCycleBleeding.length > 0) {
        actualCurrentPeriodEnd = daysBetween(lastPeriodStart, currentCycleBleeding[0]) + 1;
        for (let i = 1; i < currentCycleBleeding.length; i++) {
            const gap = daysBetween(currentCycleBleeding[i - 1], currentCycleBleeding[i]);
            if (gap <= 2) {
                actualCurrentPeriodEnd = daysBetween(lastPeriodStart, currentCycleBleeding[i]) + 1;
            }
            else {
                break; // gap too large — mid-cycle bleeding, not part of the period
            }
        }
    }
    const effectivePeriodLength = Math.max(actualCurrentPeriodEnd, averagePeriodLength);
    // ─── Roll forward through un-logged cycles ──────────────────────────
    // This app is prediction-first: a logging gap doesn't mean the cycle
    // stopped, it means the user didn't log (missing data ≠ no cycle). So if
    // today is past the last logged cycle's length, project predicted cycles
    // forward — mirroring getCalendarDaysForMonth's wrap — so "today" maps to
    // the CURRENT predicted cycle, not a stale one. Within the logged cycle
    // (rawCycleDay <= length) this is a no-op and behaviour is unchanged.
    // The last logged cycle has no measured length (no next start), so its
    // nominal length is the average. resolveCycleDay rolls today forward through
    // any gap — the same helper the calendar uses, so the two never diverge.
    const { cycleDay: currentCycleDay, projected: currentCycleProjected } = resolveCycleDay(rawCycleDay, averageCycleLength, averageCycleLength);
    // Projected cycles use the average (predicted) period length; the live logged
    // cycle uses its actual contiguous bleeding length. Matches the calendar.
    const periodLengthForPhase = currentCycleProjected ? averagePeriodLength : effectivePeriodLength;
    const currentPhase = getPhaseForDay(currentCycleDay, averageCycleLength, periodLengthForPhase);
    // Anchor everything to the current cycle's start (rolled forward), so the next
    // period is always a FUTURE date and daysUntilNextPeriod is never "overdue".
    const currentCycleStart = addDays(today, -(currentCycleDay - 1));
    const nextPeriodStart = addDays(currentCycleStart, averageCycleLength);
    const daysUntilNextPeriod = daysBetween(today, nextPeriodStart);
    // Contraception context
    const isOnContraception = !!contraception && contraception.category !== 'none' && contraception.category !== 'other';
    // Suppress fertile window for hormonal methods (ovulation suppressed or unreliable)
    const fertileWindowSuppressed = isOnContraception &&
        contraception.hormonalType !== 'non_hormonal';
    // Fertile window estimation — skip for hormonal BC
    const fertile = fertileWindowSuppressed
        ? { ovulationDay: 0, fertileStart: 0, fertileEnd: 0, method: 'calendar' }
        : estimateFertileWindow(cycleEntries, lastPeriodStart, averageCycleLength);
    // currentCycleDay is already normalized to [1, averageCycleLength] above.
    const isFertileToday = fertileWindowSuppressed
        ? false
        : currentCycleDay >= fertile.fertileStart && currentCycleDay <= fertile.fertileEnd;
    const isOvulationToday = fertileWindowSuppressed
        ? false
        : currentCycleDay === fertile.ovulationDay;
    // Luteal phase starts day after ovulation
    const lutealPhaseStart = fertileWindowSuppressed ? null : fertile.ovulationDay + 1;
    const daysUntilLutealPhase = lutealPhaseStart ? lutealPhaseStart - currentCycleDay : null;
    // Days on contraception method
    const daysOnMethod = isOnContraception && contraception.startDate
        ? daysBetween(contraception.startDate, today)
        : null;
    // Confidence tier based on tracked period count
    const trackedPeriodCount = periodStartDates.length;
    const { tier: confidenceTier, label: confidenceLabel } = getConfidenceTier(trackedPeriodCount, hasSetupData);
    return {
        currentCycleDay,
        currentPhase,
        currentCycleProjected,
        averageCycleLength,
        averagePeriodLength,
        lastPeriodStart,
        nextPeriodStart,
        daysUntilNextPeriod,
        periodStartDates,
        estimatedOvulationDay: fertileWindowSuppressed ? null : fertile.ovulationDay,
        fertileWindowStart: fertileWindowSuppressed ? null : fertile.fertileStart,
        fertileWindowEnd: fertileWindowSuppressed ? null : fertile.fertileEnd,
        fertileWindowMethod: fertile.method,
        isFertileToday,
        isOvulationToday,
        lutealPhaseStart,
        daysUntilLutealPhase: daysUntilLutealPhase != null && daysUntilLutealPhase > 0 ? daysUntilLutealPhase : null,
        confidenceTier,
        confidenceLabel,
        trackedPeriodCount,
        isOnContraception,
        contraceptionCategory: contraception?.category ?? null,
        fertileWindowSuppressed,
        daysOnMethod,
    };
}
export function getCalendarDaysForMonth(year, month, prediction, cycleEntries) {
    const today = toDateStr(new Date());
    const firstOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDow = firstOfMonth.getDay(); // 0=Sun
    // Build a map of one entry per date (last in array wins), then derive
    // bleeding dates from the map — NOT the raw array. A Set only adds, so
    // iterating the raw array would keep a stale bleeding:true even after
    // the user edited that entry to bleeding:false.
    const entryByDate = new Map();
    for (const e of cycleEntries) {
        entryByDate.set(e.date, e);
    }
    const bleedingDates = new Set();
    const spottingDates = new Set();
    const sortedBleedingDates = [];
    for (const [date, entry] of entryByDate) {
        if (isActiveBleeding(entry)) {
            bleedingDates.add(date);
            if (isSpottingOnly(entry))
                spottingDates.add(date);
        }
    }
    for (const d of bleedingDates)
        sortedBleedingDates.push(d);
    sortedBleedingDates.sort();
    // Precompute per-cycle actual data so each cycle uses its own period length
    // and cycle length rather than global averages. Averages are only used for
    // predicted cycles where we have no actual data.
    //
    // IMPORTANT: missing data ≠ negative data. If the gap between two known
    // period starts is unreasonably large (>1.5× average), the user likely just
    // didn't log — NOT that they had one giant cycle. In that case, fall back to
    // the average cycle length and project predicted cycles through the gap.
    const cycleActualData = new Map();
    if (prediction) {
        const periodStarts = prediction.periodStartDates;
        for (let i = 0; i < periodStarts.length; i++) {
            const start = periodStarts[i];
            // Actual period length: count contiguous bleeding days from this start
            let periodLen = 0;
            let lastBd = '';
            for (const bd of sortedBleedingDates) {
                if (bd < start)
                    continue;
                if (periodLen === 0) {
                    periodLen = 1;
                    lastBd = bd;
                }
                else {
                    const gap = daysBetween(lastBd, bd);
                    if (gap <= 2) {
                        periodLen = daysBetween(start, bd) + 1;
                        lastBd = bd;
                    }
                    else {
                        break;
                    }
                }
            }
            // Actual cycle length: only trust the real gap if it's within a
            // reasonable range (18-45 days). Larger gaps indicate missed logging,
            // not a single long cycle — use the average instead.
            let cycleLen = prediction.averageCycleLength;
            if (i < periodStarts.length - 1) {
                const rawGap = daysBetween(start, periodStarts[i + 1]);
                if (rawGap >= MIN_CYCLE_LENGTH && rawGap <= MAX_CYCLE_LENGTH) {
                    cycleLen = rawGap;
                }
            }
            // For the current/last cycle (i === periodStarts.length - 1), the period
            // may still be in progress — don't shrink the menstrual window below the
            // average. Only completed cycles can have shorter-than-average periods.
            const isCurrentCycle = i === periodStarts.length - 1;
            const effectivePeriodLen = periodLen || prediction.averagePeriodLength;
            cycleActualData.set(start, {
                periodLength: isCurrentCycle
                    ? Math.max(effectivePeriodLen, prediction.averagePeriodLength)
                    : effectivePeriodLen,
                cycleLength: cycleLen,
            });
        }
    }
    // Precompute "period ended early" — if a user logged no bleeding on a
    // predicted menstrual day AND there's no subsequent bleeding still within
    // the menstrual window, that day marks the early end. Crucially, a single
    // no-bleeding day mid-period (e.g., Feb 5 between bleeding on Feb 4 & 6)
    // should NOT trigger early-end — bleeding resumes, so the period continues.
    const earlyEndDate = new Map(); // cycleStart → first no-bleed date
    if (prediction) {
        const periodStarts = prediction.periodStartDates;
        for (const [date, entry] of entryByDate) {
            if (isActiveBleeding(entry))
                continue; // only care about explicit no-bleeding logs
            // Find which cycle this date belongs to
            let cycleStart = null;
            for (let i = periodStarts.length - 1; i >= 0; i--) {
                if (date >= periodStarts[i]) {
                    cycleStart = periodStarts[i];
                    break;
                }
            }
            if (!cycleStart && date >= prediction.lastPeriodStart)
                cycleStart = prediction.lastPeriodStart;
            if (!cycleStart)
                continue;
            const data = cycleActualData.get(cycleStart);
            const cycleLenForCycle = data?.cycleLength ?? prediction.averageCycleLength;
            const periodLenForCycle = data?.periodLength ?? prediction.averagePeriodLength;
            const cycleDay = daysBetween(cycleStart, date) + 1;
            const predictedPhase = getPhaseForDay(cycleDay, cycleLenForCycle, periodLenForCycle);
            if (predictedPhase === 'menstrual') {
                // Only mark early-end if no bleeding exists AFTER this date within
                // the same cycle's menstrual window. If there IS later bleeding,
                // this is just a gap day (e.g., light day with no logging), not
                // the end of the period.
                let hasLaterBleedingInWindow = false;
                for (const bd of sortedBleedingDates) {
                    if (bd <= date)
                        continue;
                    const bdCycleDay = daysBetween(cycleStart, bd) + 1;
                    if (bdCycleDay > periodLenForCycle)
                        break; // past menstrual window
                    hasLaterBleedingInWindow = true;
                    break;
                }
                if (!hasLaterBleedingInWindow) {
                    const prev = earlyEndDate.get(cycleStart);
                    if (!prev || date < prev)
                        earlyEndDate.set(cycleStart, date);
                }
            }
        }
    }
    const days = [];
    // Pad leading empty days
    for (let i = 0; i < startDow; i++) {
        const d = new Date(year, month, 1 - (startDow - i));
        days.push({
            date: toDateStr(d),
            phase: null,
            hasBleeding: false,
            isSpotting: false,
            hasEntry: false,
            isToday: false,
            isPredicted: false,
            isFertile: false,
            isOvulation: false,
        });
    }
    for (let day = 1; day <= daysInMonth; day++) {
        const date = toDateStr(new Date(year, month, day));
        const entry = entryByDate.get(date);
        const hasEntry = !!entry;
        const hasBleeding = bleedingDates.has(date);
        const isToday = date === today;
        let phase = null;
        let isPredicted = false;
        let isFertile = false;
        let isOvulation = false;
        if (entry?.cyclePhase && entry.bleeding !== false && entry.flowLevel !== 'none') {
            phase = entry.cyclePhase;
        }
        if (prediction) {
            // Project phase from nearest period start
            const periodStarts = prediction.periodStartDates;
            let cycleStart = null;
            for (let i = periodStarts.length - 1; i >= 0; i--) {
                if (date >= periodStarts[i]) {
                    cycleStart = periodStarts[i];
                    break;
                }
            }
            if (!cycleStart && date >= prediction.lastPeriodStart) {
                cycleStart = prediction.lastPeriodStart;
            }
            if (!cycleStart && date >= prediction.nextPeriodStart) {
                cycleStart = prediction.nextPeriodStart;
            }
            if (cycleStart) {
                // Use per-cycle actual data when available, fall back to averages
                // for future/predicted cycles
                const data = cycleActualData.get(cycleStart);
                let cycleLenForDay = data?.cycleLength ?? prediction.averageCycleLength;
                let periodLenForDay = data?.periodLength ?? prediction.averagePeriodLength;
                // Roll past the known cycle's length into predicted cycles (missing
                // data ≠ no cycle). resolveCycleDay is shared with the ring prediction
                // so today's phase is computed identically on both surfaces.
                const rawDay = daysBetween(cycleStart, date) + 1;
                const resolved = resolveCycleDay(rawDay, cycleLenForDay, prediction.averageCycleLength);
                const cycleDay = resolved.cycleDay;
                if (resolved.projected) {
                    cycleLenForDay = prediction.averageCycleLength;
                    periodLenForDay = prediction.averagePeriodLength;
                }
                if (cycleDay >= 1 && cycleDay <= cycleLenForDay) {
                    if (!phase) {
                        const predictedPhase = getPhaseForDay(cycleDay, cycleLenForDay, periodLenForDay);
                        // If user explicitly logged no bleeding on a predicted menstrual day,
                        // or if the period ended early (no-bleed log on an earlier menstrual
                        // day in this cycle), override to follicular — actual data beats predictions.
                        // BUT: a gap day mid-period (no-bleed with later bleeding still in the
                        // menstrual window) stays menstrual — periods can have light/skip days.
                        if (predictedPhase === 'menstrual') {
                            const endDate = cycleStart ? earlyEndDate.get(cycleStart) : undefined;
                            // Check if there's later bleeding in this cycle's menstrual window
                            let hasLaterBleedingInWindow = false;
                            if (hasEntry && !hasBleeding && cycleStart) {
                                for (const bd of sortedBleedingDates) {
                                    if (bd <= date)
                                        continue;
                                    const bdDay = daysBetween(cycleStart, bd) + 1;
                                    if (bdDay > periodLenForDay)
                                        break;
                                    hasLaterBleedingInWindow = true;
                                    break;
                                }
                            }
                            if ((hasEntry && !hasBleeding && !hasLaterBleedingInWindow) || (endDate && date >= endDate)) {
                                phase = 'follicular';
                                isPredicted = !hasEntry;
                            }
                            else {
                                phase = predictedPhase;
                                isPredicted = !hasEntry;
                            }
                        }
                        else {
                            phase = predictedPhase;
                            isPredicted = !hasEntry;
                        }
                    }
                    // Fertile window projection — use per-cycle day within the
                    // effective cycle so it projects into predicted cycles too
                    if (prediction.fertileWindowStart != null && prediction.fertileWindowEnd != null) {
                        isFertile = cycleDay >= prediction.fertileWindowStart && cycleDay <= prediction.fertileWindowEnd;
                        isOvulation = cycleDay === prediction.estimatedOvulationDay;
                    }
                }
            }
        }
        const isSpotting = spottingDates.has(date);
        days.push({ date, phase, hasBleeding, isSpotting, hasEntry, isToday, isPredicted, isFertile, isOvulation });
    }
    // Extend menstrual phase to cover adjacent logged period bleeding days.
    // Only period-level bleeding (light/medium/heavy) extends the phase —
    // spotting adjacent to a period is clinically distinct (e.g. pre-period
    // spotting) and should show as a dot, not solid pink.
    // Forward pass: period bleeding after menstrual → menstrual
    for (let i = 1; i < days.length; i++) {
        if (days[i].hasBleeding && !days[i].isSpotting && days[i].phase !== 'menstrual' && days[i - 1].phase === 'menstrual') {
            days[i].phase = 'menstrual';
        }
    }
    // Backward pass: period bleeding before menstrual → menstrual
    for (let i = days.length - 2; i >= 0; i--) {
        if (days[i].hasBleeding && !days[i].isSpotting && days[i].phase !== 'menstrual' && days[i + 1].phase === 'menstrual') {
            days[i].phase = 'menstrual';
        }
    }
    return days;
}
